import { lazy, Suspense, type ReactElement } from "react";
import { createBrowserRouter, Navigate } from "react-router";

import { AppLayout } from "./app-layout";
import { RouteError } from "./RouteError";
import { RequireAuth } from "./guards/RequireAuth";
import { CandidateDetailRoute } from "./routes/candidate-detail";
import { CompanyClassesRoute } from "./routes/company-classes";
import { ControllingRoute } from "./routes/controlling";
import { DashboardRoute } from "./routes/dashboard";
import { ForgotPasswordRoute } from "./routes/forgot-password";
import { JobCreateRoute } from "./routes/job-create";
import { JobEditRoute } from "./routes/job-edit";
import { LoginRoute } from "./routes/login";
import { LonglistRoute } from "./routes/longlist";
import { RecommendationRoute } from "./routes/recommendation";
import { ResetPasswordRoute } from "./routes/reset-password";
import { SettingsRoute } from "./routes/settings";

// Heavy routes are code-split so the main bundle stays under the
// 400 kB gzip budget (CLAUDE.md §7). FoundationsRoute is a dev-only
// design-system reference; RecommendationPdfRoute pulls @react-pdf/
// renderer via its own lazy boundary, but keeping the route shell out
// of the main chunk trims a couple of kB of eager imports too.
const FoundationsRoute = lazy(() =>
  import("./routes/foundations").then((m) => ({ default: m.FoundationsRoute })),
);
const RecommendationPdfRoute = lazy(() =>
  import("./routes/recommendation/pdf").then((m) => ({
    default: m.RecommendationPdfRoute,
  })),
);

function LazyRoute({ children }: { children: ReactElement }): ReactElement {
  return (
    <Suspense
      fallback={
        <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8">
          <p className="text-sm text-text-secondary" role="status">
            Lade…
          </p>
        </main>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Route tree.
 *
 * Unauthenticated: `/login`, `/forgot-password`. LoginRoute redirects to
 * /dashboard when a session is already set.
 *
 * Authenticated shell: `<RequireAuth>` + `<AppLayout>` (TopNav + Outlet).
 * Feature routes follow MVP_requirements.md §4:
 *   Screen 1 Dashboard         /dashboard
 *   Screen 2 JobCreate         /jobs/new
 *   Screen 3 Longlist          /jobs/:jobId/longlist
 *   Screen 4 CandidateDetail   /jobs/:jobId/candidates/:candidateId
 *   Screen 5 Recommendation    /jobs/:jobId/recommendation
 *   Screen 5 PDF-Export        /jobs/:jobId/recommendation/pdf
 *            Settings           /settings
 *
 * `/foundations` stays around as a dev-only design-system reference —
 * filtered out of the TopNav in prod builds via AppLayout. */
export const router = createBrowserRouter([
  {
    // Attach the global error boundary at the top of the tree so it
    // catches render failures in every child route, including
    // unauthenticated ones.
    errorElement: <RouteError />,
    children: [
      // Unauthenticated
      { path: "/login", Component: LoginRoute },
      { path: "/forgot-password", Component: ForgotPasswordRoute },
      { path: "/reset-password", Component: ResetPasswordRoute },

      // Authenticated shell
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { path: "/", element: <Navigate to="/dashboard" replace /> },
              { path: "/dashboard", Component: DashboardRoute },
              { path: "/jobs/new", Component: JobCreateRoute },
              { path: "/jobs/:jobId/edit", Component: JobEditRoute },
              { path: "/jobs/:jobId/longlist", Component: LonglistRoute },
              {
                path: "/jobs/:jobId/candidates/:candidateId",
                Component: CandidateDetailRoute,
              },
              { path: "/jobs/:jobId/recommendation", Component: RecommendationRoute },
              {
                path: "/jobs/:jobId/recommendation/pdf",
                element: (
                  <LazyRoute>
                    <RecommendationPdfRoute />
                  </LazyRoute>
                ),
              },
              { path: "/controlling", Component: ControllingRoute },
              { path: "/firmenklassen", Component: CompanyClassesRoute },
              { path: "/settings", Component: SettingsRoute },
              {
                path: "/foundations",
                element: (
                  <LazyRoute>
                    <FoundationsRoute />
                  </LazyRoute>
                ),
              },
            ],
          },
        ],
      },

      // 404 catch-all: render the error boundary directly so the user
      // sees a proper German "Seite nicht gefunden" view instead of a
      // silent redirect.
      { path: "*", Component: RouteError },
    ],
  },
]);
